import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const ParallaxImages = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const backY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const frontY = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <div ref={ref} className="relative mx-auto h-[640px] w-full max-w-[520px] sm:h-[620px]">
      <motion.figure style={{ y: backY }} className="absolute left-0 top-0 w-[62%]">
        <div className="aspect-[4/5] overflow-hidden border border-gold/20">
          <img src="/images/heritage/scene_00.jpg" alt="An emperor enthroned in a traditional embroidered kaba"
            className="h-full w-full object-cover sepia-[0.45] grayscale-[0.3]" loading="lazy" />
        </div>
        <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Then · the imperial court</figcaption>
      </motion.figure>
      <motion.figure style={{ y: frontY }} className="absolute bottom-0 right-0 w-[60%]">
        <div className="aspect-[3/4] overflow-hidden rounded-t-full border border-gold/50 shadow-[0_30px_70px_rgba(0,0,0,0.75)]">
          <img src="/images/real/navy_worn.jpg" alt="A woman wearing the Midnight Majesty kaba"
            className="h-full w-full object-cover" loading="lazy" />
        </div>
        <figcaption className="mt-3 text-right font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Now · today’s woman</figcaption>
      </motion.figure>
    </div>
  );
};

const HISTORY = [
  { src: "/images/history/h1.jpg", alt: "An Ethiopian emperor enthroned in a red velvet kaba heavy with gold embroidery", caption: "Imperial red velvet, threaded in gold" },
  { src: "/images/history/h2.jpg", alt: "A crowned royal woman in a purple velvet kaba with jewelled gold embroidery", caption: "Royal purple, jewelled and crowned" },
];

const History = () => (
  <div data-testid="history-gallery" className="mt-28 border-t border-gold/15 pt-16 lg:mt-36">
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
      <Reveal>
        <Eyebrow>From the history of the kaba</Eyebrow>
        <p className="mt-5 max-w-md font-display text-3xl font-light leading-tight text-cream sm:text-4xl">
          Once reserved for <span className="italic text-white">clergy and royalty</span>.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {HISTORY.map((h, i) => (
          <Reveal key={h.src} delay={i * 0.1}>
            <figure data-testid={`history-photo-${i + 1}`}>
              <div className="aspect-[3/4] overflow-hidden rounded-t-full border border-gold/30">
                <img src={h.src} alt={h.alt} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out hover:scale-[1.05]" />
              </div>
              <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-stone2">{h.caption}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

export const Intro = () => (
  <section id="story" data-testid="intro-section" className="relative mx-auto max-w-[1400px] px-5 py-28 sm:px-10 lg:py-40">
    <div className="grid items-center gap-20 lg:grid-cols-[1fr_1fr] lg:gap-24">
      <div>
        <Reveal><Eyebrow>Reinventing the kaba</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            A garment of <span className="italic text-white">holiness</span> and crowns, made for her.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p data-testid="intro-paragraph" className="mt-10 max-w-xl text-base font-light leading-[1.85] text-cream/85 sm:text-lg">
            <span className="float-left mr-3 mt-1 font-display text-7xl leading-[0.8] text-gold">H</span>
            ilmay’s Kabas are a modern reinterpretation of a traditional garment steeped in holiness, royalty, and cultural heritage.
            Once reserved for clergy and royalty, these lavish, regal garments have been reimagined for today’s woman.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mt-6 max-w-xl text-base font-light leading-[1.85] text-stone2 sm:text-lg">
            Our modern creations celebrate the trendy, bold, and fashion-forward woman who embraces elegance, knows her worth,
            and proudly honors where she comes from.
          </p>
        </Reveal>
      </div>
      <ParallaxImages />
    </div>
    <History />
  </section>
);
